import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Layout, 
  Palette, 
  PenTool, 
  Code, 
  Users, 
  Download, 
  FileText, 
  DollarSign,
  Clock,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const nicheIcons = {
  web_design: Layout,
  branding: Palette,
  copywriting: PenTool,
  development: Code,
  consulting: Users,
};

const nicheLabels = {
  web_design: 'Web Design',
  branding: 'Branding',
  copywriting: 'Copywriting',
  development: 'Development',
  consulting: 'Consulting',
};

const nicheColors = {
  web_design: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  branding: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  copywriting: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  development: 'bg-green-500/20 text-green-300 border-green-500/30',
  consulting: 'bg-pink-500/20 text-pink-300 border-pink-500/30',
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNiche, setSelectedNiche] = useState('all');
  const [importing, setImporting] = useState(null);

  async function loadTemplates() {
    try {
      let url = `${API_URL}/api/templates`;
      if (selectedNiche && selectedNiche !== 'all') {
        url += `?niche=${selectedNiche}`;
      }
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.items || []);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTemplates();
  }, [selectedNiche]);

  async function handleImport(templateId) {
    try {
      setImporting(templateId);
      const response = await fetch(`${API_URL}/api/templates/import/${templateId}`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Template imported! Proposal and contract created.');
        // Navigate to the new proposal
        if (data.proposal_id) {
          navigate(`/proposals`);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to import template');
      }
    } catch (error) {
      toast.error('Failed to import template');
    } finally {
      setImporting(null);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-700 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-slate-700 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-indigo-400" />
            Template Library
          </h1>
          <p className="text-slate-400 mt-1">
            Pre-built proposal and contract bundles to jumpstart your projects
          </p>
        </div>
        <Select value={selectedNiche} onValueChange={setSelectedNiche}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Filter by niche" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Categories</SelectItem>
            <SelectItem value="web_design" className="text-white">Web Design</SelectItem>
            <SelectItem value="branding" className="text-white">Branding</SelectItem>
            <SelectItem value="copywriting" className="text-white">Copywriting</SelectItem>
            <SelectItem value="development" className="text-white">Development</SelectItem>
            <SelectItem value="consulting" className="text-white">Consulting</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((tpl) => {
          const Icon = nicheIcons[tpl.niche] || FileText;
          const pricing = tpl.proposal?.pricing || {};
          
          return (
            <Card 
              key={tpl._id} 
              className="bg-slate-800/50 border-slate-700 hover:border-indigo-500/50 transition-colors overflow-hidden"
            >
              {/* Preview Image */}
              {tpl.preview_image && (
                <div className="h-32 overflow-hidden">
                  <img 
                    src={tpl.preview_image} 
                    alt={tpl.title}
                    className="w-full h-full object-cover opacity-80"
                  />
                </div>
              )}
              
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className={nicheColors[tpl.niche] || 'bg-slate-500/20 text-slate-300'}>
                    <Icon className="w-3 h-3 mr-1" />
                    {nicheLabels[tpl.niche] || tpl.niche}
                  </Badge>
                </div>
                <CardTitle className="text-white text-lg mt-2">{tpl.title}</CardTitle>
                <p className="text-slate-400 text-sm line-clamp-2">{tpl.description}</p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Quick Info */}
                <div className="flex items-center gap-4 text-sm">
                  {pricing.total && (
                    <div className="flex items-center gap-1 text-green-400">
                      <DollarSign className="w-4 h-4" />
                      <span>${pricing.total.toLocaleString()}</span>
                      {pricing.recurring && <span className="text-slate-500">/mo</span>}
                    </div>
                  )}
                  {tpl.proposal?.timeline && (
                    <div className="flex items-center gap-1 text-slate-400">
                      <Clock className="w-4 h-4" />
                      <span>{tpl.proposal.timeline}</span>
                    </div>
                  )}
                </div>

                {/* Deliverables Preview */}
                {tpl.proposal?.deliverables && (
                  <div className="space-y-1">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Includes</p>
                    <div className="flex flex-wrap gap-1">
                      {tpl.proposal.deliverables.slice(0, 3).map((d, i) => (
                        <span key={i} className="text-xs bg-slate-700/50 text-slate-300 px-2 py-1 rounded">
                          {d}
                        </span>
                      ))}
                      {tpl.proposal.deliverables.length > 3 && (
                        <span className="text-xs text-slate-500 px-2 py-1">
                          +{tpl.proposal.deliverables.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Import Button */}
                <Button
                  onClick={() => handleImport(tpl._id)}
                  disabled={importing === tpl._id}
                  className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  {importing === tpl._id ? (
                    <>Importing...</>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Use This Template
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {templates.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">No templates found</p>
          <p className="text-slate-500 text-sm mt-1">
            {selectedNiche !== 'all' ? 'Try selecting a different category' : 'Templates will appear here'}
          </p>
        </div>
      )}
    </div>
  );
}
